
import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Box, Typography, IconButton, Stack } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

/**
 * Componente que muestra una imagen simple o un carrusel automático,
 * con opción de mostrar una descripción y fondo personalizado.
 * Si solo hay una imagen, el texto (si existe) va arriba y la imagen ocupa el ancho completo.
 * @param {object[]} images - Array de objetos imagen (Contentful asset).
 * @param {string} description - Texto descriptivo opcional.
 * @param {number} interval - Intervalo en ms para el cambio automático (solo carrusel).
 * @param {string} background - Valor CSS para el fondo (color, gradiente, imagen, etc).
 */
const ImageCarouselBlock = ({
  images = [],
  description = "",
  interval = 6000,
  background = "",
}) => {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);

  const isCarousel = images.length > 1;
  const isSingleImage = images.length === 1;

  // Cambio automático solo si hay más de una imagen
  useEffect(() => {
    if (isCarousel) {
      timerRef.current = setInterval(() => {
        setCurrent((prev) => (prev + 1) % images.length);
      }, interval);
      return () => clearInterval(timerRef.current);
    }
  }, [images.length, interval, isCarousel]);

  // Cambio manual
  const handlePrev = () => setCurrent((prev) => (prev - 1 + images.length) % images.length);
  const handleNext = () => setCurrent((prev) => (prev + 1) % images.length);

  if (!images.length) return null;

  // Layout para una sola imagen: texto arriba, imagen full width
  if (isSingleImage) {
    const img = images[0];
    return (
      <Box
        sx={{
          my: { xs: 2, md: 4 },
          px: { xs: 0, md: 0 },
          width: "100%",
          ...(background
            ? { background, borderRadius: 3, py: { xs: 2, md: 4 } }
            : {}),
        }}
      >
        {description && (
          <Typography
            variant="h4"
            component="div"
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "24px", md: "28px" },
              mb: 2,
              textAlign: "center",
            }}
          >
            {description}
          </Typography>
        )}
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <img
            src={`https:${img.fields.file.url}`}
            alt={img.fields.title || "Imagen"}
            style={{
              width: "100%",
              maxWidth: "1200px",
              maxHeight: 600,
              borderRadius: 12,
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              objectFit: "contain",
            }}
          />
        </Box>
        {img.fields.description && (
          <Typography variant="caption" display="block" sx={{ mt: 1, color: "text.secondary", textAlign: "center" }}>
            {img.fields.description}
          </Typography>
        )}
      </Box>
    );
  }

  // Render de la imagen/carrusel (más de una imagen)
  const renderCarousel = () => {
    const img = images[current];
    return (
      <Box sx={{ position: "relative", textAlign: "center" }}>
        <img
          src={`https:${img.fields.file.url}`}
          alt={img.fields.title || `Imagen ${current + 1}`}
          style={{
            maxWidth: "100%",
            maxHeight: 400,
            borderRadius: 12,
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            transition: "opacity 0.5s",
          }}
        />
        {/* Flechas de navegación */}
        <IconButton
          onClick={handlePrev}
          sx={{
            position: "absolute",
            top: "50%",
            left: 8,
            transform: "translateY(-50%)",
            bgcolor: "rgba(255,255,255,0.7)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
          }}
          aria-label="Anterior"
        >
          <ArrowBackIosNewIcon />
        </IconButton>
        <IconButton
          onClick={handleNext}
          sx={{
            position: "absolute",
            top: "50%",
            right: 8,
            transform: "translateY(-50%)",
            bgcolor: "rgba(255,255,255,0.7)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
          }}
          aria-label="Siguiente"
        >
          <ArrowForwardIosIcon />
        </IconButton>
        {/* Leyenda */}
        {img.fields.description && (
          <Typography variant="caption" display="block" sx={{ mt: 1, color: "text.secondary" }}>
            {img.fields.description}
          </Typography>
        )}
        {/* Indicadores */}
        <Box sx={{ mt: 1 }}>
          {images.map((_, idx) => (
            <span
              key={idx}
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: idx === current ? "#1976d2" : "#bbb",
                margin: "0 4px",
                transition: "background 0.3s",
              }}
            />
          ))}
        </Box>
      </Box>
    );
  };

  // Layout: dos columnas si hay descripción, centrado si no
  const containerSx = {
    my: { xs: 1, md: 4 },
    mx: { xs: '16px', md: '24px' },
    px: { xs: '16px', md: '24px' },
    ...(background
      ? { background, borderRadius: 3, py: { xs: 2, md: 4 } }
      : {}),
  };

  if (description) {
    return (
      <Box sx={containerSx}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={4}
          alignItems="center"
          justifyContent="center"
        >
          {/* Columna de texto */}
          <Box sx={{ flex: 1, textAlign: { xs: "center", md: "left" } }}>
            <Typography
              variant="h4"
              component="div"
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "24px", md: "24px" },
                mb: { xs: 1, md: 0 },
              }}
            >
              {description}
            </Typography>
          </Box>
          {/* Columna de carrusel */}
          <Box sx={{ flex: 1, width: "100%" }}>
            {renderCarousel()}
          </Box>
        </Stack>
      </Box>
    );
  }

  // Si no hay descripción, centrado
  return (
    <Box sx={{ ...containerSx, display: "flex", justifyContent: "center", alignItems: "center" }}>
      {renderCarousel()}
    </Box>
  );
};

ImageCarouselBlock.propTypes = {
  images: PropTypes.arrayOf(PropTypes.object).isRequired,
  description: PropTypes.string,
  interval: PropTypes.number,
  background: PropTypes.string,
};

export default ImageCarouselBlock;
